<template>
  <div class="app-layout">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ 'sidebar-open': isSidebarOpen }" />
    <div class="main-content">
      <div class="course-details-container" v-if="course">
        <!-- Hero Section -->
        <div class="hero-section">
          <div class="hero-content">
            <div class="breadcrumb">
              <router-link to="/courses">Courses</router-link> / {{ course.title }}
            </div>
            <div class="title-section">
              <h1>{{ course.title }}</h1>
              <h2 class="arabic-title" v-if="course.title_ar">{{ course.title_ar }}</h2>
            </div>
            <div class="hero-badges">
              <span :class="['status-badge', course.status]">{{ course.status }}</span>
              <span :class="['premium-badge', course.is_premium ? 'premium' : 'free']">
                {{ course.is_premium ? 'Premium' : 'Free' }}
              </span>
              <span v-if="course.top_course" class="top-course-badge">
                <i class="fas fa-star"></i> Top Course
              </span>
              <button 
                class="edit-course-button"
                @click="editCourse"
                v-if="$route.path !== `/courses/edit/${course.id}`">
                <i class="fas fa-edit"></i> Edit Course
              </button>
            </div>
          </div>
        </div>

        <!-- Main Grid Layout -->
        <div class="content-grid">
          <!-- Left Column -->
          <div class="main-column">
            <!-- Course Description Card -->
            <div class="card description-card">
              <div class="card-header">
                <h3>About this course</h3>
              </div>
              <div class="card-content">
                <div class="description" v-html="formatDescription(course.description)"></div>
                <div class="description arabic" v-if="course.description_ar" v-html="formatDescription(course.description_ar)"></div>
              </div>
            </div>

            <!-- Video Section -->
            <div class="card video-card" v-if="course.science_behind_it">
              <div class="card-header">
                <h3>Science Behind It</h3>
              </div>
              <div class="video-container">
                <video controls :src="course.science_behind_it">
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <!-- Lessons Section -->
            <div class="card lessons-card" v-if="course.lessons?.length">
              <div class="card-header">
                <div class="lessons-header">
                  <h3>Course Content</h3>
                </div>
                <div class="lessons-controls">
                  <div class="search-container">
                    <input 
                      type="text" 
                      v-model="searchQuery" 
                      placeholder="Search lessons..."
                      class="search-input"
                    >
                    <i class="fas fa-search search-icon"></i>
                  </div>
                  <span class="lesson-count">{{ filteredLessons.length }} of {{ course.lessons.length }} lessons</span>
                </div>
              </div>
              <div class="lessons-grid">
                <div v-for="lesson in filteredLessons" :key="lesson.id" class="lesson-item">
                  <div class="lesson-thumbnail">
                    <img :src="lesson.lesson_thumbnail_url" :alt="lesson.title">
                    <span class="duration">
                      <i class="far fa-clock"></i> {{ formatDuration(lesson.duration) }}
                    </span>
                    <span v-if="lesson.is_hidden" class="hidden-badge">
                      <i class="fas fa-eye-slash"></i> Hidden
                    </span>
                  </div>
                  <div class="lesson-content">
                    <h4>{{ lesson.title }}</h4>
                    <p class="lesson-description">{{ lesson.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="side-column">
            <!-- Course Stats Card -->
            <div class="card stats-card">
              <div class="stat-item">
                <i class="fas fa-clock"></i>
                <div class="stat-content">
                  <span class="stat-label">Duration</span>
                  <span class="stat-value">{{ course.minutes }} minutes</span>
                </div>
              </div>
              <div class="stat-item">
                <i class="fas fa-folder"></i>
                <div class="stat-content">
                  <span class="stat-label">Category</span>
                  <span class="stat-value">{{ course.category?.title }}</span>
                </div>
              </div>
              <div class="stat-item">
                <i class="fas fa-calendar-alt"></i>
                <div class="stat-content">
                  <span class="stat-label">Last Updated</span>
                  <span class="stat-value">{{ formatDate(course.updated_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Course Files Component -->
            <CourseFiles 
              :courseId="course.id"
              :authToken="authToken"
            />

            <!-- Instructor Card -->
            <div class="card instructor-card" v-if="course.instructor">
              <div class="card-header">
                <h3>Meet Your Instructor</h3>
              </div>
              <div class="instructor-content">
                <div class="instructor-stats">
                  <div class="rating">
                    <i class="fas fa-star"></i>
                    <span>{{ course.instructor.rating }}</span>
                    <small>({{ course.instructor.total_reviews }} reviews)</small>
                  </div>
                  <div class="session-info">
                    <div class="session-item">
                      <i class="fas fa-clock"></i>
                      <span>{{ course.instructor.session_duration }}min/session</span>
                    </div>
                    <div class="session-item">
                      <i class="fas fa-dollar-sign"></i>
                      <span>${{ course.instructor.price_per_session }}/session</span>
                    </div>
                  </div>
                </div>
                <div class="instructor-bio" v-html="course.instructor.bio"></div>
                <div class="instructor-bio arabic" v-if="course.instructor.bio_ar" v-html="course.instructor.bio_ar"></div>
                <div class="instructor-specialties" v-if="course.instructor.specialties?.length">
                  <h4>Specialties</h4>
                  <div class="specialty-tags">
                    <span v-for="specialty in course.instructor.specialties" 
                          :key="specialty" 
                          class="specialty-tag">
                      {{ specialty }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Students Card -->
            <div class="card students-card" v-if="course.students?.length">
              <div class="card-header">
                <h3>Enrolled Students</h3>
                <span class="student-count">{{ course.students.length }} students</span>
              </div>
              <div class="students-list">
                <div v-for="student in course.students" 
                     :key="student.id" 
                     class="student-item">
                  <div class="student-info">
                    <img v-if="student.img_url" 
                         :src="student.img_url" 
                         :alt="student.first_name"
                         class="student-avatar">
                    <div class="student-details">
                      <span class="student-name">
                        {{ student.first_name }} {{ student.last_name }}
                      </span>
                      <span class="student-email">{{ student.email }}</span>
                    </div>
                  </div>
                  <div class="completion-info">
                    <div class="completion-bar">
                      <div class="progress" 
                           :style="{ width: student.pivot.completion_percentage + '%' }"
                           :class="{ 
                             'high': student.pivot.completion_percentage >= 70,
                             'medium': student.pivot.completion_percentage >= 30 && student.pivot.completion_percentage < 70,
                             'low': student.pivot.completion_percentage < 30 
                           }">
                      </div>
                      <span class="completion-text">{{ student.pivot.completion_percentage }}%</span>
                    </div>
                    <span class="last-login">Last login: {{ formatDate(student.last_login_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import CourseFiles from '@/components/CourseFiles.vue';

export default {
  name: 'CourseDetails',
  components: {
    Sidebar,
    CourseFiles
  },
  data() {
    return {
      course: null,
      loading: false,
      error: null,
      isSidebarOpen: false,
      searchQuery: '',
      authToken: localStorage.getItem('token'),
    };
  },
  computed: {
    filteredLessons() {
      if (!this.course?.lessons) return [];
      return this.course.lessons.filter(lesson => {
        const search = this.searchQuery.toLowerCase();
        return lesson.title.toLowerCase().includes(search) ||
               lesson.description.toLowerCase().includes(search);
      });
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatDescription(text) {
      if (!text) return '';
      return text.replace(/\r\n/g, '<br>');
    },
    async fetchCourseDetails() {
      try {
        this.loading = true;
        const token = localStorage.getItem('token');
        
        // Fetch course details
        const courseResponse = await axios.get(
          `https://api.kewaar.com/api/admin/courses/${this.$route.params.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        // Fetch lessons for this course
        const lessonsResponse = await axios.get(
          `https://api.kewaar.com/api/admin/courses/${this.$route.params.id}/lessons`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        this.course = {
          ...courseResponse.data.data,
          lessons: lessonsResponse.data.data
        };
        
      } catch (error) {
        this.error = error.message;
        console.error('Error fetching course details:', error);
      } finally {
        this.loading = false;
      }
    },
    editCourse() {
      this.$router.push(`/courses/edit/${this.course.id}`);
    },
    async saveCourseEdits(updatedCourse) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `https://api.kewaar.com/api/admin/courses/${this.course.id}`,
          updatedCourse,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        await this.fetchCourseDetails(); // Refresh the data
      } catch (error) {
        console.error('Error updating course:', error);
      }
    },
  },
  created() {
    this.fetchCourseDetails();
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  min-height: 100vh;
}

.course-details-container {
  max-width: 1920px;
  margin: 0 auto;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(to right, #2c5282, #2b6cb0);
  color: white;
  padding: 3rem 2rem;
  margin-bottom: 2rem;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
}

.breadcrumb {
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.breadcrumb a {
  color: #e2e8f0;
  text-decoration: none;
}

.title-section h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.arabic-title {
  font-size: 1.5rem;
  color: #e2e8f0;
  margin-top: 0.5rem;
}

.hero-badges {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 0 2rem;
  max-width: 1600px;
  margin: 0 auto;
}

/* Cards */
.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #2d3748;
}

.card-content {
  padding: 1.5rem;
}

/* Description Card */
.description {
  color: #4a5568;
  line-height: 1.6;
}

.description.arabic {
  direction: rtl;
  text-align: right;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

/* Stats Card */
.stats-card {
  padding: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-item i {
  font-size: 1.25rem;
  color: #4299e1;
  margin-right: 1rem;
  width: 24px;
  text-align: center;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
}

.stat-value {
  font-weight: 500;
  color: #2d3748;
}

/* Badges */
.status-badge,
.premium-badge,
.top-course-badge {
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge.active {
  background: #c6f6d5;
  color: #2f855a;
}

.premium-badge.premium {
  background: #fed7d7;
  color: #c53030;
}

.premium-badge.free {
  background: #e2e8f0;
  color: #4a5568;
}

.top-course-badge {
  background: #fefcbf;
  color: #975a16;
}

/* Lessons Grid */
.lessons-grid {
  display: grid;
  gap: 1.5rem;
  padding: 1.5rem;
}

.lesson-item {
  display: flex;
  gap: 1.5rem;
  background: #f7fafc;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s;
}

.lesson-item:hover {
  transform: translateY(-2px);
}

.lesson-thumbnail {
  position: relative;
  width: 200px;
  height: 150px;
  flex-shrink: 0;
}

.lesson-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lesson-content {
  padding: 1rem;
  flex: 1;
}

.lesson-content h4 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
}

.lesson-description {
  color: #718096;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

/* Instructor Card */
.instructor-card .instructor-content {
  padding: 1.5rem;
}

.instructor-stats {
  margin-bottom: 1.5rem;
}

.rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.rating i {
  color: #ecc94b;
}

.session-info {
  display: flex;
  gap: 1rem;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #718096;
  font-size: 0.875rem;
}

.instructor-bio {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.specialty-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.specialty-tag {
  background: #ebf4ff;
  color: #4299e1;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
}

/* Students List */
.students-list {
  padding: 1.5rem;
}

.student-item {
  padding: 1rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.student-item:last-child {
  border-bottom: none;
}

.student-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.student-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.student-details {
  display: flex;
  flex-direction: column;
}

.student-name {
  font-weight: 500;
  color: #2d3748;
}

.student-email {
  font-size: 0.875rem;
  color: #718096;
}

.completion-info {
  margin-top: 0.5rem;
}

.completion-bar {
  height: 8px;
  background: #edf2f7;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  margin-bottom: 0.5rem;
}

.completion-bar .progress {
  height: 100%;
  transition: width 0.3s ease;
}

.completion-bar .progress.high {
  background: #48bb78;
}

.completion-bar .progress.medium {
  background: #ecc94b;
}

.completion-bar .progress.low {
  background: #f56565;
}

.completion-text {
  font-size: 0.75rem;
  color: #718096;
}

.last-login {
  font-size: 0.75rem;
  color: #a0aec0;
}

/* Video Section */
.video-container {
  padding: 1.5rem;
  background: #1a202c;
}

.video-container video {
  width: 100%;
  border-radius: 8px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .side-column {
    order: -1;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .hero-section {
    padding: 2rem 1rem;
  }

  .content-grid {
    padding: 0 1rem;
  }

  .lesson-item {
    flex-direction: column;
  }

  .lesson-thumbnail {
    width: 100%;
  }

  .session-info {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .hero-badges {
    flex-wrap: wrap;
  }

  .title-section h1 {
    font-size: 2rem;
  }
}

/* Add to existing styles */
.edit-course-button {
  background: #4299e1;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
}

.edit-course-button:hover {
  background: #3182ce;
}

.edit-course-button i {
  font-size: 0.875rem;
}

/* Lessons Header */
.lessons-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.lessons-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  width: 100%;
}

.search-container {
  position: relative;
  flex-grow: 1;
  max-width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #718096;
}

.lesson-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
}

.edit-lesson, .delete-lesson {
  padding: 0.25rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #718096;
}

.edit-lesson:hover {
  color: #4299e1;
}

.delete-lesson:hover {
  color: #f56565;
}
</style> 